/**
 * Moving an event between calendars (Personal ⇄ team:<id>) — card fbb1b6c8.
 *
 * Every calendar is its own extension_documents scope, and the Rust records
 * upsert REFUSES a record id that already lives in another scope
 * (scope_mismatch). So a move is never "save the same uid elsewhere": it is a
 * CREATE with a fresh uid in the target scope, and the old copy is deleted
 * only after every create has succeeded. If anything is refused, the copies
 * already written are rolled back and the original is left untouched — the
 * failure costs the user a retry, never the appointment.
 *
 * A recurring master takes its override records along (they link to the
 * master by `override_of.master_uid` and would otherwise be orphaned in the
 * old scope).
 */
import { saveEvent, deleteEvent } from './calendarStore';
import { assertCanWriteCalendar } from './teamWriteCheck';
import { carryFiredReminders } from '../../../services/calendarReminderScheduler';

const keyOf = (calendarKey) => calendarKey || 'personal';
const warnLeftBehind = (err) => console.warn('[calendar] move: an old copy could not be deleted', err);

/** True when saving `fields` over `previous` changes the event's calendar. */
export function isCalendarMove(previous, fields) {
  if (!previous?.uid) return false;
  return keyOf(previous.calendar) !== keyOf(fields?.calendar);
}

/**
 * Move `fields` (the edited event, still carrying its OLD uid) out of
 * `fromCalendar` into `fields.calendar`. `overrides` are the master's
 * override records (pass [] for a single event). Returns the saved event
 * (with its NEW uid). Throws when the move could not be completed; the
 * original then still exists.
 */
export async function moveEvent(fields, fromCalendar, overrides = []) {
  const oldUid = fields.uid;
  const toCalendar = keyOf(fields.calendar);
  await assertCanWriteCalendar(toCalendar); // the server's answer, not the cached role
  const created = [];
  try {
    const saved = await saveEvent({
      ...fields,
      uid: null,
      calendar: toCalendar,
    });
    created.push(saved.uid);
    const renamed = [[oldUid, saved.uid]];
    for (const o of overrides) {
      // eslint-disable-next-line no-await-in-loop
      const copy = await saveEvent({
        ...o,
        uid: null,
        calendar: toCalendar,
        override_of: { ...o.override_of, master_uid: saved.uid },
      });
      created.push(copy.uid);
      renamed.push([o.uid, copy.uid]);
    }
    // A reminder that already rang must not ring again under the new uid.
    renamed.forEach(([from, to]) => carryFiredReminders(from, to));
    // Every copy exists in the target calendar — only now remove the old
    // ones. A failed delete leaves a duplicate, never a loss.
    for (const o of overrides) {
      // eslint-disable-next-line no-await-in-loop
      await deleteEvent(o.uid, keyOf(o.calendar || fromCalendar)).catch(warnLeftBehind);
    }
    await deleteEvent(oldUid, keyOf(fromCalendar)).catch(warnLeftBehind);
    return saved;
  } catch (err) {
    for (const uid of created) {
      // eslint-disable-next-line no-await-in-loop
      await deleteEvent(uid, toCalendar).catch((e) =>
        console.warn('[calendar] move: rollback could not remove a copy', e),
      );
    }
    throw err;
  }
}

export const OVERRIDE_MOVE = 'override_move';

/** Save an edited event: a plain upsert, or a move when its calendar changed. */
export function saveOrMoveEvent(previous, fields, overrides = []) {
  if (!isCalendarMove(previous, fields)) return saveEvent(fields);
  // A single exception of a series cannot move on its own: the base
  // occurrence would come back in the old calendar next to an unlinked copy
  // (card 25e3e5a2). The editor locks the picker; this is the backstop.
  if (previous.override_of) {
    const err = new Error('A single occurrence of a series cannot move to another calendar. Move the whole series instead.');
    err.code = OVERRIDE_MOVE;
    return Promise.reject(err);
  }
  return moveEvent(fields, previous.calendar, overrides);
}
